import { Pipe, PipeTransform } from '@angular/core';
import { config } from '../../config/appConfig';
import {
  ISubscriptionPlanWithTaxAndDiscountInfo,
  OrderStatus,
  PaymentStatus,
} from '../models/Subscription';

export const extractFileName = (url: string): string | null => {
  if (!url) return null;
  const fullName = url.split('/').pop()?.split('?')[0] || '';
  return decodeURIComponent(fullName).replace(
    /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
    '',
  );
};

export const getRawUrlHelper = (fileUrl: string): string => {
  try {
    // If it's already a raw path (doesn't start with http), return as-is
    if (!fileUrl.startsWith('http')) {
      return fileUrl;
    }

    const parsed = new URL(fileUrl);

    // If the URL has a ?fileUrl=... param, extract and return it
    const rawPath = parsed.searchParams.get('fileUrl');
    if (rawPath) {
      return rawPath;
    }

    const base = config.mediaDeliveryService; // Doesn't work
    if (fileUrl.startsWith(base)) {
      return fileUrl.replace(base, '');
    }

    // Fallback to pathname
    return parsed.pathname;
  } catch (error) {
    console.error('getRawUrlHelper: invalid URL', fileUrl, error);
    return fileUrl;
  }
};

// Get the Discount Percentage For A Plan (Floor Value)
export const getDiscountedPercentage = (
  sub: ISubscriptionPlanWithTaxAndDiscountInfo | null,
): number | null =>
  sub && sub.FinalAmountWithoutDiscount > 0
    ? Math.round(
        ((sub.FinalAmountWithoutDiscount - sub.FinalAmount) /
          sub.FinalAmountWithoutDiscount) *
          100,
      )
    : null;

// Gets the Discounted Price For the Plan
export const getDiscountedPrice = (
  selectedSubscription: ISubscriptionPlanWithTaxAndDiscountInfo | null,
): number | null => {
  return selectedSubscription
    ? selectedSubscription.FinalAmount - selectedSubscription.GST
    : null;
};

export const getDownloadUrl = (fileUrl: string): string => {
  return config.mediaDeliveryService + '/download' + '?fileUrl=' + fileUrl;
};

export const getPaymentStatusLabel = (paymentStatus: string): string => {
  switch (paymentStatus) {
    case PaymentStatus.PaymentPending:
      return 'Pending';
    case PaymentStatus.PartiallyPaid:
      return 'Partially Paid';
    case PaymentStatus.PaymentFailed:
      return 'Failed';
    case PaymentStatus.Paid:
      return 'Paid';
    default:
      return '-';
  }
};

export const getOrderStatusLabel = (orderStatus: string): string => {
  switch (orderStatus) {
    case OrderStatus.Draft:
      return 'Draft';
    case OrderStatus.Cancelled:
      return 'Cancelled';
    case OrderStatus.InProgress:
      return 'In Progress';
    case OrderStatus.OnHold:
      return 'On Hold';
    case OrderStatus.Returned:
      return 'Returned';
    case OrderStatus.Completed:
      return 'Completed';
    default:
      return '-';
  }
};

export const areObjectsWithSameData = (
  a: any,
  b: any,
  omitProps: string[] = [], // array of property that should not be compared
): boolean => {
  if (a === b) return true;

  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false;
  }

  // Handle arrays
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++) {
      if (!areObjectsWithSameData(a[i], b[i], omitProps)) return false;
    }
    return true;
  }

  const keysA = Object.keys(a).filter((k) => !omitProps.includes(k));
  const keysB = Object.keys(b).filter((k) => !omitProps.includes(k));

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!areObjectsWithSameData(a[key], b[key], omitProps)) return false;
  }

  return true;
};

export function getCurrencySymbol(currency: string, locale = 'en'): string {
  if (!currency) {
    return '';
  }
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).formatToParts(0);

  return parts.find((p) => p.type === 'currency')?.value || currency;
}

// Formats an amount with locale-aware grouping (Indian grouping for INR, e.g. 1,47,712.50)
// Always two decimals (e.g. 883.90); hideWholeDecimals drops ".00" for whole amounts only (e.g. 1,743 but 700.50)
export function formatAmount(
  amount: number | null | undefined,
  currency: string = 'INR',
  hideWholeDecimals: boolean = false,
): string {
  if (amount === null || amount === undefined || isNaN(Number(amount))) {
    return '';
  }
  const value = Number(amount);
  const fractionDigits =
    hideWholeDecimals && Number.isInteger(Math.round(value * 100) / 100)
      ? 0
      : 2;
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

// Checks whether a billing cycle is a one-time purchase ("One-Time", "OneTime", "one_time", ...)
export const isOneTimeBillingCycle = (billingCycle?: string | null): boolean =>
  (billingCycle ?? '').toLowerCase().replace(/[^a-z]/g, '') === 'onetime';

@Pipe({
  name: 'transformUrl',
  standalone: true,
})
export class TransformUrlPipe implements PipeTransform {
  transform(
    url: string | null | undefined,
    defaultUrl: string = '/assets/images/DefaultThumbnail.png',
  ): string {
    if (!url) {
      return defaultUrl;
    }
    return getDownloadUrl(getRawUrlHelper(url));
  }
}
